import { useEffect, useRef, useState } from 'react';
import { sessionsApi } from '../../../../api/sessionsApi';

const MIN_GAP_SECONDS = 10;
const TASK_POLL_MS = 5000;
const SINGLE_TASK_ATTEMPTS = 60;
const BATCH_TASK_ATTEMPTS = 120;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const formatGapTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mmss = `${String(m).padStart(h ? 2 : 1, '0')}:${String(s).padStart(2, '0')}`;
  return h ? `${h}:${mmss}` : mmss;
};

/**
 * "Fix missing sections": find silent gaps in the transcript and re-transcribe
 * them one by one or all at once. Each gap carries a `_status` of
 * processing / done / failed while it is being fixed.
 */
export function useTranscriptGaps(id, language, notify) {
  const [isOpen, setIsOpen] = useState(false);
  const [gaps, setGaps] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [startingIdx, setStartingIdx] = useState(null);
  const [isFixingAll, setIsFixingAll] = useState(false);
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  const setGapStatus = (update) => setGaps((prev) => (prev ? { ...prev, gaps: prev.gaps.map(update) } : prev));

  /** Polls a Celery task; resolves with its status, or null when it is still running after `attempts`. */
  const waitForTask = async (taskId, attempts) => {
    for (let i = 0; i < attempts && alive.current; i += 1) {
      await sleep(TASK_POLL_MS);
      try {
        const status = await sessionsApi.getTaskStatus(taskId);
        if (status.ready) return status;
      } catch {
        // Network blip, keep polling.
      }
    }
    return null;
  };

  const open = async () => {
    setIsOpen(true);
    setIsDetecting(true);
    setGaps(null);
    try {
      setGaps(await sessionsApi.detectTranscriptGaps(id, MIN_GAP_SECONDS));
    } catch (e) {
      notify({ type: 'error', title: 'Failed to detect gaps', message: e.message });
      setGaps({ gaps: [], gap_count: 0 });
    } finally {
      setIsDetecting(false);
    }
  };

  const fixOne = async (gap, idx) => {
    const range = `${formatGapTime(gap.start_time)} - ${formatGapTime(gap.end_time)}`;
    setStartingIdx(idx);
    let taskId;
    try {
      const result = await sessionsApi.regenerateSection(id, gap.start_time, gap.end_time, null, language);
      taskId = result.task_id;
      notify({ type: 'info', title: 'Section regeneration started', message: `Regenerating ${range}...` });
      setGapStatus((g, i) => (i === idx ? { ...g, _status: 'processing' } : g));
    } catch (e) {
      notify({ type: 'error', title: 'Failed to start section regeneration', message: e.message });
      return;
    } finally {
      setStartingIdx(null);
    }
    if (!taskId) return;

    const status = await waitForTask(taskId, SINGLE_TASK_ATTEMPTS);
    if (!alive.current) return;
    if (!status) {
      notify({ type: 'warning', title: 'Regeneration timeout', message: 'Task is still running. Check back later.' });
    } else if (status.error) {
      setGapStatus((g, i) => (i === idx ? { ...g, _status: 'failed' } : g));
      notify({ type: 'error', title: 'Section regeneration failed', message: status.error?.slice(0, 200) });
    } else {
      setGapStatus((g, i) => (i === idx ? { ...g, _status: 'done' } : g));
      const added = status.result?.new_segments_count ?? 0;
      notify({ type: 'success', title: 'Section regenerated successfully', message: `${range}: ${added} new segments added` });
    }
  };

  const fixAll = async () => {
    const list = gaps?.gaps || [];
    if (!list.length) return;
    setIsFixingAll(true);
    setGapStatus((g) => ({ ...g, _status: 'processing' }));
    try {
      const result = await sessionsApi.regenerateAllSections(id, list, null, language);
      notify({ type: 'info', title: 'Batch regeneration started', message: `Processing ${list.length} gaps sequentially...` });
      if (!result.task_id) return;

      const status = await waitForTask(result.task_id, BATCH_TASK_ATTEMPTS);
      if (!alive.current) return;
      if (!status) {
        notify({ type: 'warning', title: 'Timeout', message: 'Batch task is still running. Check back later.' });
      } else if (status.error) {
        notify({ type: 'error', title: 'Batch regeneration failed', message: status.error?.slice(0, 200) });
        setGapStatus((g) => ({ ...g, _status: 'failed' }));
      } else {
        const r = status.result || {};
        notify({
          type: 'success',
          title: 'All sections regenerated',
          message: `${r.succeeded}/${r.total_gaps} gaps fixed, ${r.total_new_segments} new segments added`,
        });
        const results = r.results || [];
        setGapStatus((g, i) => ({ ...g, _status: results[i]?.status === 'success' ? 'done' : 'failed' }));
      }
    } catch (e) {
      notify({ type: 'error', title: 'Failed to start batch regeneration', message: e.message });
      setGapStatus((g) => ({ ...g, _status: undefined }));
    } finally {
      if (alive.current) setIsFixingAll(false);
    }
  };

  return {
    isOpen,
    open,
    close: () => setIsOpen(false),
    gaps,
    isDetecting,
    startingIdx,
    isFixingAll,
    fixOne,
    fixAll,
  };
}
